/**
 * The plan's stationing, read off the plan as an inspector reads it: the station labels
 * ("SC1: BC: 81+80.72", "57+00", "SC2 ℄ STA: 100+62.02"), each followed along its leader to the
 * point it names on an alignment's centreline; the centreline itself, followed through its dashes,
 * ticks and curve points; and the stations along it, fitted to the labels' positions — so the
 * stations of any point along the line can be measured, exactly, where the plan names none.
 *
 * Nothing is guessed. A reading of the plan is kept only when it is borne out by the rest of it:
 *  - a label is read only when every glyph of its station is read with confidence;
 *  - an alignment's stationing is kept only when at least three labels (two, when their scale is
 *    the scale the plan's other alignments share) lie on its centreline where a single straight
 *    measure along the line puts them, to a fraction of a foot — a misread digit, a leader
 *    followed to the wrong line, a centreline that turned onto another at a crossing, each puts a
 *    label off that measure, and it is left out, or the alignment with it;
 *  - the labels on one alignment must name it alike (SC1 … SC1): if two names turn up, it is left
 *    out altogether.
 * Where the reading does not hold, the plan has no stationing, and nothing is filled in.
 *
 * All positions are image px; the text was found in working px (the analysed raster, scale k).
 */
import type { Vec } from '../../shared/doc.ts';
import { IntentEngine } from './engine.ts';
import { lumaAt, type Luma } from './ink.ts';
import { findTextLines, type TextLine } from './labels.ts';
import { CHARS, glyphFeatures, glyphProbs, lineGlyphs, ocrReady } from './ocr.ts';
import type { Component } from './raster.ts';
import { arcBetween, type ArcStretch } from './arcs.ts';
import { traceCentreline } from './centreline.ts';
import { projectOnRail, type Rail } from './rails.ts';
import type { PlanRaster, Seg, VectorPlan } from './types.ts';

/** A station label read off the plan. */
export interface StationLabel {
  feet: number;
  text: string; // as read: "SC1:BC:81+80.72"
  station: string; // "81+80.72"
  name: string | null; // the alignment it names ("SC1"), when it names one
  kind: string | null; // "BC", "PRC", "STA", "CL" (a ℄ STA label: a point off the centreline, perhaps) …
  conf: number; // the least sure of its station's glyphs
  box: [number, number, number, number];
  H: number; // cap height
}

/** One alignment's stationing: its centreline, and the station at every point along it. */
export interface Alignment {
  name: string | null;
  pts: number[]; // the centreline, image px
  cum: number[]; // arc length at each vertex
  origin: number; // station (ft) at arc 0
  sign: 1 | -1; // +1: stations grow along pts
  pxPerFt: number;
  /** The labels it was fitted to: where each lies along the line, its station, and how far off the fit it is (ft). */
  anchors: { s: number; feet: number; resid: number; text: string; x: number; y: number }[];
  /**
   * The points of known station along it, in order: its labels, and the station ticks between and
   * beyond them (each at its round station). Stations are measured between them, and no further
   * than `reach` beyond the first and last.
   */
  knots: { s: number; feet: number; tick: boolean }[];
  /** The interval its ticks are drawn at (ft), or null when it has none to go by. */
  tickEvery: number | null;
  /** How far past its first and last knots a station is still measured (px): a tick's interval, or a few feet. */
  reach: number;
}

/** What the page is sent of a plan's stationing (the rest is the reader's working). */
export type PlanStations = Pick<Stationing, 'alignments' | 'labels' | 'ms'>;

export interface Stationing {
  alignments: Alignment[];
  labels: StationLabel[];
  /** Leaders followed (debugging): from the label to the point named. */
  leaders: { text: string; start: [number, number]; tip: [number, number]; point: [number, number] | null; rail: string | null }[];
  /** How the anchors fell into lines and what each line's fit made of them (debugging). */
  groups: string[];
  /** Every centreline traced (debugging). */
  traces: number[][];
  /** The ticks found along each alignment, as the stations its fit puts them at (debugging). */
  ticks: string[];
  /** Every anchor: the point a label names, the line's direction there, and whether a circle marked it (debugging). */
  anchorsSeen: { text: string; point: [number, number] | null; tangent: [number, number] | null; circle: boolean }[];
  ms: number;
}

const idx = (c: string) => CHARS.indexOf(c);
const DIGITS = [...'0123456789'].map(idx);
/** Letters a digit is misread as, and the digit (in a station, only a digit can stand). */
const ALIAS: [number, number][] = [
  [idx('O'), 0],
  [idx('D'), 0],
  [idx('Q'), 0],
  [idx('I'), 1],
  [idx('Z'), 2],
  [idx('S'), 5],
  [idx('G'), 6],
  [idx('B'), 8],
];
const PLUS = idx('+'),
  DOT = idx('.'),
  COLON = idx(':'),
  CL = idx('℄'),
  JUNK = idx('~');
/** Curve and point names that stand between an alignment's name and a station. */
const KEYWORDS = new Set(['BC', 'EC', 'PC', 'PT', 'PI', 'PRC', 'PCC', 'POC', 'POT', 'POB', 'POE', 'STA', 'BVC', 'EVC', 'PVI', 'PVC', 'PVT', 'TS', 'ST', 'CS', 'SC', 'BEG', 'END', 'BOP', 'EOP', 'GB', 'MATCH', 'CL']);

/** A glyph read as a digit: which, how sure among the digits, and how much of it is a digit at all. */
function digitOf(p: Float32Array): { d: number; conf: number; mass: number } {
  const v = new Float64Array(10);
  for (let d = 0; d < 10; d++) v[d] = p[DIGITS[d]!]!;
  for (const [c, d] of ALIAS) v[d]! += p[c]!;
  let best = 0,
    mass = 0;
  for (let d = 0; d < 10; d++) {
    mass += v[d]!;
    if (v[d]! > v[best]!) best = d;
  }
  return { d: best, conf: mass > 0 ? v[best]! / mass : 0, mass };
}
const argmax = (p: Float32Array): number => {
  let a = 0;
  for (let c = 1; c < p.length; c++) if (p[c]! > p[a]!) a = c;
  return a;
};

const MIN_CONF = 0.85; // every glyph of a station at least this sure

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * The station a line of text states, if it states one: digits, a plus, two digits, perhaps a point
 * and one or two more, all close together; and the words before it ("SC1:", "SC1: BC:", "SC1 ℄ STA:").
 * `P` the glyph readings, `B` their boxes and `H`, `base` the line's measure (any one unit).
 */
export function parseStationLine(P: Float32Array[], B: Box[], H: number, base: number): Omit<StationLabel, 'box' | 'H'> | null {
  const n = P.length;
  const gapBefore = (i: number) => B[i]!.x0 - B[i - 1]!.x1;
  const tall = (i: number) => B[i]!.y1 - B[i]!.y0 >= 0.6 * H;
  for (let j = 1; j < n - 2; j++) {
    const pj = P[j]!;
    if (argmax(pj) !== PLUS || pj[PLUS]! < 0.5) continue;
    const near = 0.8 * H;
    // the whole feet before the plus
    const left: { d: number; conf: number }[] = [];
    let i = j - 1;
    while (i >= 0 && left.length < 4) {
      if (gapBefore(i + 1) > near || !tall(i)) break;
      const dg = digitOf(P[i]!);
      if (dg.mass < 0.6) break;
      left.unshift({ d: dg.d, conf: dg.conf * Math.min(1, dg.mass / 0.9) });
      i--;
    }
    const start = i + 1;
    if (!left.length) continue;
    // two digits of feet after it
    const right: { d: number; conf: number }[] = [];
    let k = j + 1;
    while (k < n && right.length < 2) {
      if (gapBefore(k) > near || !tall(k)) break;
      const dg = digitOf(P[k]!);
      if (dg.mass < 0.6) break;
      right.push({ d: dg.d, conf: dg.conf * Math.min(1, dg.mass / 0.9) });
      k++;
    }
    if (right.length !== 2) continue;
    // a decimal point and hundredths, perhaps
    const frac: { d: number; conf: number }[] = [];
    if (k < n && gapBefore(k) <= near) {
      const b = B[k]!;
      const small = b.y1 - b.y0 <= 0.4 * H && b.x1 - b.x0 <= 0.45 * H && b.y1 >= base - 0.3 * H;
      if (small && P[k]![DOT]! >= 0.3) {
        let m = k + 1;
        while (m < n && frac.length < 2) {
          if (gapBefore(m) > near || !tall(m)) break;
          const dg = digitOf(P[m]!);
          if (dg.mass < 0.6) break;
          frac.push({ d: dg.d, conf: dg.conf * Math.min(1, dg.mass / 0.9) });
          m++;
        }
        // stations are written to the hundredth: a point with fewer digits after it is a label read short (a glyph lost under a line)
        if (frac.length !== 2) continue;
      }
    }
    const digits = [...left, ...right, ...frac];
    const conf = Math.min(pj[PLUS]!, ...digits.map((d) => d.conf));
    const whole = left.map((d) => d.d).join('');
    const station = `${Number(whole)}+${right.map((d) => d.d).join('')}${frac.length ? '.' + frac.map((d) => d.d).join('') : ''}`;
    const feet = Number(whole) * 100 + Number(right.map((d) => d.d).join('') + (frac.length ? '.' + frac.map((d) => d.d).join('') : ''));
    // the words before it: split at colons, the centreline sign and spaces
    const tokens: { text: string; conf: number }[] = [];
    let cur = '',
      curConf = 1,
      centre = false;
    const flush = () => {
      if (cur) tokens.push({ text: cur, conf: curConf });
      cur = '';
      curConf = 1;
    };
    for (let q = 0; q < start; q++) {
      const a = argmax(P[q]!);
      if (q > 0 && gapBefore(q) > 0.55 * H) flush();
      if (a === COLON) {
        flush();
        continue;
      }
      if (a === CL) {
        flush();
        centre = true;
        continue;
      }
      if (a === JUNK || a === DOT) {
        flush();
        continue;
      }
      cur += CHARS[a];
      curConf = Math.min(curConf, P[q]![a]!);
    }
    flush();
    // a word read unsurely is no name
    const words = tokens.filter((t) => /^[A-Z0-9/-]+$/.test(t.text));
    let name: string | null = null,
      kind: string | null = centre ? 'CL' : null;
    const isKey = (t: string) => KEYWORDS.has(t);
    if (centre) {
      // "SC1 ℄ STA:": the name, then STA
      const before = words.filter((w) => !isKey(w.text));
      if (before.length) name = before[before.length - 1]!.text;
    } else if (words.length >= 2) {
      const w0 = words[words.length - 2]!,
        w1 = words[words.length - 1]!;
      if (isKey(w1.text) && !isKey(w0.text)) {
        name = w0.text;
        kind = w1.text;
      } else if (!isKey(w1.text)) name = w1.text;
      else kind = w1.text;
    } else if (words.length === 1) {
      if (isKey(words[0]!.text)) kind = words[0]!.text;
      else name = words[0]!.text;
    }
    const nameConf = name ? Math.min(...words.filter((w) => w.text === name).map((w) => w.conf)) : 1;
    if (name && (nameConf < 0.7 || name.length > 8 || !/[A-Z]/.test(name))) name = null;
    const text = P.slice(0, k + frac.length + (frac.length ? 1 : 0)).map((p) => CHARS[argmax(p)]).join('');
    return { feet, text, station, name, kind, conf };
  }
  return null;
}

/* ------------------------------------------------------------------ leaders */

const MAX_LEADER = 2600; // px

function segEnd(s: Seg, atA: boolean): Vec {
  return atA ? { x: s.pts[0]!, y: s.pts[1]! } : { x: s.pts[s.pts.length - 2]!, y: s.pts[s.pts.length - 1]! };
}

/**
 * Follow a leader from where it leaves the text, as the eye does, along its ink: out along its
 * landing (the short level stroke beside the text), round its one bend, then straight on through
 * every line it crosses, to its tip. `end` is the text's end at its mid-height and `out` the way
 * away from the text. Null when no leader leaves the text there.
 */
export function followLeader(plan: PlanRaster, engine: IntentEngine | null, end: Vec, out: 1 | -1, H: number): { tip: Vec; dir: Vec; segs: Set<number>; start: Vec; gap: number; landing: Vec } | null {
  const { lineMask, cw, ch, k } = plan;
  const ink = (x: number, y: number): boolean => {
    const X = Math.floor(x * k),
      Y = Math.floor(y * k);
    return X >= 0 && Y >= 0 && X < cw && Y < ch && lineMask[Y * cw + X] === 1;
  };
  const px = 1 / k; // one working pixel, in image px
  // where the leader's ink begins: its landing, on the text's mid-line just beyond it
  let from: Vec | null = null;
  for (let d = 0; d <= 1.6 * H && !from; d += 0.5 * px)
    for (const dy of [0, -px, px, -2 * px, 2 * px]) if (ink(end.x + out * d, end.y + dy)) {
      from = { x: end.x + out * d, y: end.y + dy };
      break;
    }
  if (!from) return null;
  // the landing: ink running level away from the text
  const level = marchInk(plan, from, { x: out, y: 0 }, 6 * H, false);
  const landLen = level ? Math.abs(level.tip.x - from.x) : 0;
  let corner = from;
  let skipLevel = false;
  if (level && landLen >= 0.3 * H && landLen <= 4 * H) {
    corner = level.tip;
    skipLevel = true; // the leader leaves the landing at an angle
  } else if (level && landLen > 4 * H) {
    // a long level run is the leader itself (a level leader)
    const run = marchInk(plan, from, { x: out, y: 0 }, MAX_LEADER, true);
    if (!run) return null;
    return { tip: run.tip, dir: run.dir, segs: leaderPieces(plan, engine, from, run), start: from, gap: Math.abs(from.x - end.x), landing: from };
  }
  // the leader's direction: the one along which the ink runs furthest from the corner (tried from
  // every pixel about it: where the level ink gives out is rarely exactly where the leader begins)
  // the length of ink along a ray from o (a pixel either side of it), until it gives out
  const inkRun = (o: Vec, u: Vec, reach: number): number => {
    let len = 0,
      miss = 0;
    const nx = -u.y * px,
      ny = u.x * px;
    for (let t = px; t <= reach; t += 0.5 * px) {
      const x = o.x + u.x * t,
        y = o.y + u.y * t;
      if (ink(x, y) || ink(x + nx, y + ny) || ink(x - nx, y - ny)) {
        len = t;
        miss = 0;
      } else if ((miss += 0.5 * px) > 1.5 * px) break;
    }
    return len;
  };
  const allowed = (a: number) => {
    const u = { x: Math.cos(a), y: Math.sin(a) };
    if (u.x * out < -0.2) return false; // back over the text
    if (skipLevel && Math.abs(u.y) < 0.17) return false; // the landing's own line
    return true;
  };
  // the middle of the directions along which the ink runs furthest (a line a pixel or two wide
  // is run along alike by a small fan of them)
  const scan = (o: Vec, a0: number, a1: number, da: number, reach: number): { a: number; len: number } => {
    const runs: { a: number; len: number }[] = [];
    for (let a = a0; a <= a1 + 1e-9; a += da) if (allowed(a)) runs.push({ a, len: inkRun(o, { x: Math.cos(a), y: Math.sin(a) }, reach) });
    if (!runs.length) return { a: a0, len: -1 };
    const top = Math.max(...runs.map((r) => r.len));
    // the fan about the best one (never across to another line's direction)
    const bi = runs.findIndex((r) => r.len === top);
    let i0 = bi,
      i1 = bi;
    while (i0 > 0 && runs[i0 - 1]!.len >= top - 1.5 * px) i0--;
    while (i1 < runs.length - 1 && runs[i1 + 1]!.len >= top - 1.5 * px) i1++;
    return { a: (runs[i0]!.a + runs[i1]!.a) / 2, len: top };
  };
  // (a first look from the corner every 3°, then the best few origins about it at 1°)
  let pick = { o: corner, a: 0, len: -1 };
  const origins: { o: Vec; a: number; len: number }[] = [];
  for (let dy = -3; dy <= 3; dy++)
    for (let dx = -3; dx <= 3; dx++) {
      const o = { x: corner.x + dx * px, y: corner.y + dy * px };
      if (!ink(o.x, o.y)) continue;
      const r = scan(o, -Math.PI, Math.PI, Math.PI / 60, 30);
      origins.push({ o, a: r.a, len: r.len });
    }
  origins.sort((p1, q1) => q1.len - p1.len);
  for (const c of origins.slice(0, 4)) {
    const r = scan(c.o, c.a - Math.PI / 45, c.a + Math.PI / 45, Math.PI / 180, 30);
    if (r.len > pick.len + 1e-9) pick = { o: c.o, a: r.a, len: r.len };
  }
  if (pick.len < 10) return null;
  const fine = scan(pick.o, pick.a - Math.PI / 60, pick.a + Math.PI / 60, Math.PI / 1800, 90);
  const u = { x: Math.cos(fine.a), y: Math.sin(fine.a) };
  corner = pick.o;
  const run = marchInk(plan, corner, u, MAX_LEADER, true);
  if (!run || Math.hypot(run.tip.x - corner.x, run.tip.y - corner.y) < 1.2 * H) return null;
  return { tip: run.tip, dir: run.dir, segs: leaderPieces(plan, engine, corner, run), start: corner, gap: Math.abs(from.x - end.x), landing: from };
}

/** The pieces of line a leader is made of (never to be taken for the line it points at). */
function leaderPieces(_plan: PlanRaster, engine: IntentEngine | null, start: Vec, run: { tip: Vec; dir: Vec }): Set<number> {
  const used = new Set<number>();
  if (!engine) return used;
  const L = Math.hypot(run.tip.x - start.x, run.tip.y - start.y);
  const off = (q: Vec) => Math.abs((q.x - start.x) * run.dir.y - (q.y - start.y) * run.dir.x);
  const along = (q: Vec) => (q.x - start.x) * run.dir.x + (q.y - start.y) * run.dir.y;
  for (let t = 0; t <= L; t += 20) {
    const q = { x: start.x + run.dir.x * t, y: start.y + run.dir.y * t };
    for (const id of engine.segsNear(q, 12)) {
      if (used.has(id)) continue;
      const sg = engine.plan.segs[id]!;
      const a = segEnd(sg, true),
        b = segEnd(sg, false);
      if (off(a) < 2.5 && off(b) < 2.5 && along(a) > -3 && along(b) > -3 && along(a) < L + 3 && along(b) < L + 3) used.add(id);
    }
  }
  return used;
}

/**
 * Follow a straight line of ink from `p` along `u` (image px): step along it, keeping to the
 * middle of the ink, straight through other lines crossing it, until the ink ends. Returns where
 * it ends and the direction it ran in.
 */
function marchInk(plan: PlanRaster, p0: Vec, u0: Vec, maxLen: number, steer: boolean): { tip: Vec; dir: Vec } | null {
  const { lineMask, cw, ch, k } = plan;
  const ink = (x: number, y: number): boolean => {
    const X = Math.floor(x * k),
      Y = Math.floor(y * k);
    return X >= 0 && Y >= 0 && X < cw && Y < ch && lineMask[Y * cw + X] === 1;
  };
  // Along a line there is ink all the way — its own, or a line crossing it — until it ends; then paper.
  const step = 0.5 / k;
  const GAP = 1.6 / k; // a stretch of paper this long is past its end (a line is never broken so long; a line beside its end is another line)
  let u = { ...u0 };
  let p = { ...p0 },
    last: Vec = { ...p0 },
    paper = 0;
  const clean: Vec[] = [{ ...p0 }];
  const resume = (from: Vec, dir: Vec): Vec | null => {
    const n = { x: -dir.y, y: dir.x };
    let best: { p: Vec; off: number } | null = null;
    for (let d = 0.5; d <= 3; d += 0.5)
      for (let o = -2.5; o <= 2.5; o += 0.5) {
        const q = { x: from.x + (dir.x * d + n.x * o) / k, y: from.y + (dir.y * d + n.y * o) / k };
        let run = 0;
        for (let t = 0; t <= 8; t += 0.5) {
          const x = q.x + (dir.x * t) / k,
            y = q.y + (dir.y * t) / k;
          if (ink(x, y) || ink(x + (n.x * 0.5) / k, y + (n.y * 0.5) / k) || ink(x - (n.x * 0.5) / k, y - (n.y * 0.5) / k)) run++;
          else break;
        }
        if (run >= 15 && (!best || Math.abs(o) < best.off)) best = { p: q, off: Math.abs(o) };
      }
    return best ? best.p : null;
  };
  for (let i = 0; i < maxLen / step; i++) {
    const q = { x: p.x + u.x * step, y: p.y + u.y * step };
    const nx = -u.y,
      ny = u.x;
    // the ink across it, a pixel either side
    let lo = Infinity,
      hi = -Infinity;
    for (let o = -1; o <= 1; o += 0.5) if (ink(q.x + (nx * o) / k, q.y + (ny * o) / k)) {
      lo = Math.min(lo, o);
      hi = Math.max(hi, o);
    }
    p = q;
    if (lo === Infinity) {
      if ((paper += step) <= GAP) continue;
      // the ink gave out: does the line go on just beside where it was heading (a jog of a pixel
      // or two where it was drawn, or crossed)? Only a run of ink in line with it, never a line's end beside it
      const again = resume(last, u);
      if (!again) break;
      p = again;
      paper = 0;
      continue;
    }
    paper = 0;
    last = { ...p };
    // where its ink is its own (a thin run, not a crossing), keep to its middle and learn its direction
    if (steer && hi - lo <= 1.5) {
      let a0 = lo,
        a1 = hi;
      while (a0 > -3 && ink(q.x + (nx * (a0 - 0.5)) / k, q.y + (ny * (a0 - 0.5)) / k)) a0 -= 0.5;
      while (a1 < 3 && ink(q.x + (nx * (a1 + 0.5)) / k, q.y + (ny * (a1 + 0.5)) / k)) a1 += 0.5;
      if (a1 - a0 <= 2.5) {
        const c = (a0 + a1) / 2;
        const shift = Math.max(-0.25, Math.min(0.25, c)) / k;
        p = { x: q.x + nx * shift, y: q.y + ny * shift };
        clean.push({ ...p });
        if (clean.length % 20 === 0 && clean.length >= 40) {
          const tail = clean.slice(-80);
          const f = tail[0]!,
            l = tail[tail.length - 1]!;
          const dx = l.x - f.x,
            dy = l.y - f.y;
          const L = Math.hypot(dx, dy);
          if (L > 10) u = { x: dx / L, y: dy / L };
        }
      }
    }
  }
  const all = Math.hypot(last.x - p0.x, last.y - p0.y);
  if (all < 2) return null;
  return { tip: last, dir: u };
}


/**
 * The centre of a small circle (a curve point's marker, on the centreline) at a leader's tip — the
 * leader ends on its rim, or runs on into or through it — or null when there is none. Read off the
 * plan's lightness at full resolution: a circle is a ring darker all round than the paper just
 * inside and just outside it (a line or two crossing it, an arrowhead in it, change little of
 * that). Of the circles along the leader's line, the one nearest its tip that has a line (not the
 * leader) running through its centre.
 */
function circleAt(plan: VectorPlan, luma: Luma, lumaK: number, tip: Vec, u: Vec, H: number): { c: Vec; r: number; dir: Vec } | null {
  const px = 1 / plan.k;
  const rMin = Math.max(2.5, 2.5 * px),
    rMax = Math.max(4 * px, Math.min(0.8 * H, 14 * px));
  const N = 32;
  const cos = Array.from({ length: N }, (_, i) => Math.cos((2 * Math.PI * i) / N)),
    sin = Array.from({ length: N }, (_, i) => Math.sin((2 * Math.PI * i) / N));
  const dark = (x: number, y: number) => 1 - lumaAt(luma, x * lumaK, y * lumaK) / 255;
  const ring = new Float64Array(N),
    inner = new Float64Array(N),
    outer = new Float64Array(N);
  const sorted = new Float64Array(N);
  const q = (a: Float64Array, f: number) => {
    sorted.set(a);
    sorted.sort();
    return sorted[Math.min(N - 1, Math.floor(f * N))]!;
  };
  const score = (cx: number, cy: number, r: number): number => {
    // quick: most of 8 points round it dark
    let lit = 0;
    for (let i = 0; i < N; i += 4) if (Math.max(dark(cx + (r - 0.5) * cos[i]!, cy + (r - 0.5) * sin[i]!), dark(cx + (r + 0.5) * cos[i]!, cy + (r + 0.5) * sin[i]!)) < 0.3 && ++lit > 1) return -1;
    for (let i = 0; i < N; i++) {
      const c = cos[i]!,
        s2 = sin[i]!;
      ring[i] = Math.max(dark(cx + (r - 0.5) * c, cy + (r - 0.5) * s2), dark(cx + r * c, cy + r * s2), dark(cx + (r + 0.5) * c, cy + (r + 0.5) * s2));
    }
    // darker all round (its weakest stretch), than paper inside and out (but for the few lines across)
    // — a ring not dark enough all round is no circle, whatever lies inside and out of it
    const rim = q(ring, 0.15);
    if (rim < 0.35) return rim;
    for (let i = 0; i < N; i++) {
      const c = cos[i]!,
        s2 = sin[i]!;
      inner[i] = dark(cx + (r - 2) * c, cy + (r - 2) * s2);
      outer[i] = dark(cx + (r + 2) * c, cy + (r + 2) * s2);
    }
    return rim - Math.max(q(inner, 0.4), q(outer, 0.4));
  };
  type C = { x: number; y: number; r: number; s: number };
  const found: C[] = [];
  const step = Math.max(0.5, 0.5 * px);
  for (let t = -3 * rMax; t <= rMax; t += step)
    for (let o = -rMax; o <= rMax; o += step) {
      const cx = tip.x + u.x * t - u.y * o,
        cy = tip.y + u.y * t + u.x * o;
      for (let r = rMin; r <= rMax; r += 0.5) {
        if (Math.abs(o) > r + 1.5) continue;
        if (t > 0 && Math.hypot(t, o) > r + 1.5) continue;
        const sc = score(cx, cy, r);
        if (sc >= 0.35) found.push({ x: cx, y: cy, r, s: sc });
      }
    }
  if (!found.length) {
    __debug?.log?.push('no ring dark enough');
    return null;
  }
  // circles: the best fit of each place (fits about one centre are one circle)
  found.sort((m, n) => n.s - m.s);
  const circles: C[] = [];
  for (const f of found) if (!circles.some((c) => Math.hypot(c.x - f.x, c.y - f.y) <= Math.max(3, 0.8 * c.r))) circles.push(f);
  // each refined: its centre and radius to a quarter pixel
  const refined = circles.slice(0, 8).map((c) => {
    let best = c;
    for (let dx = -1; dx <= 1; dx += 0.25)
      for (let dy = -1; dy <= 1; dy += 0.25)
        for (let dr = -0.75; dr <= 0.75; dr += 0.25) {
          const sc = score(c.x + dx, c.y + dy, c.r + dr);
          if (sc > best.s) best = { x: c.x + dx, y: c.y + dy, r: c.r + dr, s: sc };
        }
    return { ...best, d: Math.hypot(best.x - tip.x, best.y - tip.y) - best.r };
  });
  refined.sort((m, n) => m.d - n.d);
  for (const c of refined) {
    const dir = lineDirAt(plan, { x: c.x, y: c.y }, c.r + 2 * px, u);
    __debug?.log?.push(`circle (${c.x.toFixed(1)},${c.y.toFixed(1)}) r ${c.r.toFixed(1)} score ${c.s.toFixed(2)} d ${c.d.toFixed(1)} ${dir ? 'on a line' : 'on no line'}`);
    if (dir) return { c: { x: c.x, y: c.y }, r: c.r, dir };
  }
  return null;
}

/**
 * The station tick at a point on a centreline: a short stroke square across the line, standing out
 * either side of it, within a few pixels along it. Its middle (on the line), or null when there is
 * no tick there.
 */
function tickAt(plan: VectorPlan, rail: Rail, p: Vec, H: number): Vec | null {
  const pr = projectOnRail(rail, p, 4);
  if (!pr) return null;
  const { lineMask, cw, ch, k } = plan;
  const ink = (x: number, y: number): boolean => {
    const X = Math.floor(x * k),
      Y = Math.floor(y * k);
    return X >= 0 && Y >= 0 && X < cw && Y < ch && lineMask[Y * cw + X] === 1;
  };
  const px = 1 / k;
  const t = { x: pr.tx, y: pr.ty },
    n = { x: -pr.ty, y: pr.tx };
  const reach = Math.max(8 * px, 0.6 * H);
  const outer = Math.min(Math.max(5 * px, 0.45 * H), 10 * px);
  // where a stroke stands out square from the line on both sides
  const at: number[] = [];
  for (let sAlong = -reach; sAlong <= reach; sAlong += 0.5 * px) {
    const c = { x: pr.x + t.x * sAlong, y: pr.y + t.y * sAlong };
    let both = true;
    for (const side of [1, -1]) {
      let run = 0;
      for (let o = 2 * px; o <= outer; o += 0.5 * px) if (ink(c.x + n.x * o * side, c.y + n.y * o * side)) run += 0.5 * px;
      if (run < 0.6 * (outer - 2 * px)) both = false;
    }
    if (both) at.push(sAlong);
  }
  if (!at.length) return null;
  // the one stroke nearest the point: its middle
  at.sort((a, b) => Math.abs(a) - Math.abs(b));
  const s0 = at[0]!;
  const same = at.filter((v) => Math.abs(v - s0) <= 3 * px);
  const lo = Math.min(...same),
    hi = Math.max(...same);
  if (hi - lo > 3 * px) return null; // a stroke, not a block
  const mid = (lo + hi) / 2;
  return { x: pr.x + t.x * mid, y: pr.y + t.y * mid };
}

/**
 * The direction of the line through p (either way along it): the one along which ink runs on both
 * sides of p — out from `skip` (a circle round p, its own rim) to a few dozen pixels — never the
 * leader (`leader`: it runs to p from one side only, and is not the line).
 */
function lineDirAt(plan: PlanRaster, p: Vec, skip: number, leader: Vec): Vec | null {
  const { lineMask, cw, ch, k } = plan;
  const ink = (x: number, y: number): boolean => {
    const X = Math.floor(x * k),
      Y = Math.floor(y * k);
    return X >= 0 && Y >= 0 && X < cw && Y < ch && lineMask[Y * cw + X] === 1;
  };
  const px = 1 / k;
  const d0 = Math.max(skip, 3 * px),
    d1 = d0 + 32 * px;
  const cover = (th: number): number => {
    const u = { x: Math.cos(th), y: Math.sin(th) },
      n = { x: -u.y * px, y: u.x * px };
    let worst = 1;
    for (const side of [1, -1]) {
      let on = 0,
        all = 0;
      for (let t = d0; t <= d1; t += px) {
        const x = p.x + u.x * t * side,
          y = p.y + u.y * t * side;
        all++;
        if (ink(x, y) || ink(x + n.x, y + n.y) || ink(x - n.x, y - n.y)) on++;
      }
      worst = Math.min(worst, on / all);
    }
    return worst;
  };
  let best = { th: 0, s: -1 };
  for (let a = 0; a < 180; a += 1) {
    const th = (a * Math.PI) / 180;
    // the leader's own direction is never the line's
    if (Math.abs(Math.cos(th) * leader.x + Math.sin(th) * leader.y) > Math.cos((12 * Math.PI) / 180)) continue;
    const s = cover(th);
    if (s > best.s) best = { th, s };
  }
  if (best.s < 0.5) return null;
  // the middle of the best directions (a line some pixels wide is covered alike over a small fan)
  const fine: { th: number; s: number }[] = [];
  for (let d = -1.5; d <= 1.5; d += 0.1) {
    const th = best.th + (d * Math.PI) / 180;
    fine.push({ th, s: cover(th) });
  }
  const peak = Math.max(...fine.map((f) => f.s));
  const top = fine.filter((f) => f.s >= peak - 0.02);
  const th = (top[0]!.th + top[top.length - 1]!.th) / 2;
  return { x: Math.cos(th), y: Math.sin(th) };
}

/**
 * How many station ticks a line has: short strokes square across it, standing out equally either
 * side (counted once each, cached per rail).
 */
function ticksOn(plan: VectorPlan, rail: Rail, cache: Map<number, number>): number {
  const hit = cache.get(rail.id);
  if (hit !== undefined) return hit;
  const { lineMask, cw, ch, k } = plan;
  const ink = (x: number, y: number): boolean => {
    const X = Math.floor(x * k),
      Y = Math.floor(y * k);
    return X >= 0 && Y >= 0 && X < cw && Y < ch && lineMask[Y * cw + X] === 1;
  };
  const px = 1 / k;
  const pts = rail.pts;
  let count = 0,
    lastAt = -Infinity,
    acc = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const ax = pts[i]!,
      ay = pts[i + 1]!,
      bx = pts[i + 2]!,
      by = pts[i + 3]!;
    const L = Math.hypot(bx - ax, by - ay);
    if (L < 1e-6) continue;
    const u = { x: (bx - ax) / L, y: (by - ay) / L },
      n = { x: -u.y, y: u.x };
    for (let t = 0; t < L; t += px) {
      const c = { x: ax + u.x * t, y: ay + u.y * t };
      // ink square across it, 2–5 px out on both sides, and paper just past that on both sides
      let ok = true;
      for (const side of [1, -1]) {
        let run = 0;
        for (let o = 2; o <= 5; o += 0.5) if (ink(c.x + n.x * o * px * side, c.y + n.y * o * px * side)) run++;
        if (run < 6) ok = false;
        if (ink(c.x + n.x * 14 * px * side, c.y + n.y * 14 * px * side)) ok = false;
        // a stroke, not a band: paper beside it along the line
        if (ink(c.x + (n.x * 3.5 * side + u.x * 3) * px, c.y + (n.y * 3.5 * side + u.y * 3) * px) && ink(c.x + (n.x * 3.5 * side - u.x * 3) * px, c.y + (n.y * 3.5 * side - u.y * 3) * px)) ok = false;
      }
      if (ok && acc + t - lastAt > 6 * px) {
        count++;
        lastAt = acc + t;
      }
    }
    acc += L;
  }
  cache.set(rail.id, count);
  return count;
}

/**
 * The station ticks along a centreline (a polyline, image px): where a short stroke crosses it
 * square, standing out equally either side. Their arc lengths along it, in order (one per tick).
 */
export function ticksAlong(plan: VectorPlan, pts: number[]): number[] {
  const { lineMask, cw, ch, k } = plan;
  const ink = (x: number, y: number): boolean => {
    const X = Math.floor(x * k),
      Y = Math.floor(y * k);
    return X >= 0 && Y >= 0 && X < cw && Y < ch && lineMask[Y * cw + X] === 1;
  };
  const px = 1 / k;
  const hits: number[] = [];
  let acc = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const ax = pts[i]!,
      ay = pts[i + 1]!,
      bx = pts[i + 2]!,
      by = pts[i + 3]!;
    const L = Math.hypot(bx - ax, by - ay);
    if (L < 1e-6) continue;
    const u = { x: (bx - ax) / L, y: (by - ay) / L },
      n = { x: -u.y, y: u.x };
    for (let t = 0; t < L; t += 0.5 * px) {
      const c = { x: ax + u.x * t, y: ay + u.y * t };
      let ok = true;
      for (const side of [1, -1]) {
        // ink standing out square from the line, 2.5–4.5 px out (allowing the trace a pixel off the line's middle)
        let run = 0;
        for (let o = 2.5; o <= 4.5; o += 0.5) {
          const x = c.x + n.x * o * px * side,
            y = c.y + n.y * o * px * side;
          if (ink(x, y) || ink(x + u.x * px, y + u.y * px) || ink(x - u.x * px, y - u.y * px)) run++;
        }
        if (run < 5) {
          ok = false;
          break;
        }
        // a stroke, not a band or another line alongside: paper beside it along the line
        const o = 3.5 * px * side;
        if (ink(c.x + n.x * o + u.x * 3 * px, c.y + n.y * o + u.y * 3 * px) && ink(c.x + n.x * o - u.x * 3 * px, c.y + n.y * o - u.y * 3 * px)) {
          ok = false;
          break;
        }
      }
      if (ok) hits.push(acc + t);
    }
    acc += L;
  }
  // one tick per run of hits: its middle
  const out: number[] = [];
  let run: number[] = [];
  for (const h of hits) {
    if (run.length && h - run[run.length - 1]! > 1.5 * px) {
      out.push((run[0]! + run[run.length - 1]!) / 2);
      run = [];
    }
    run.push(h);
  }
  if (run.length) out.push((run[0]! + run[run.length - 1]!) / 2);
  return out;
}

/** Where the ray p + t·u (t in [t0, t1]) meets a polyline, or null. */
function rayHit(pts: number[], p: Vec, u: Vec, t0: number, t1: number): { t: number; x: number; y: number } | null {
  let best: { t: number; x: number; y: number } | null = null;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const ax = pts[i]!,
      ay = pts[i + 1]!,
      bx = pts[i + 2]!,
      by = pts[i + 3]!;
    const ex = bx - ax,
      ey = by - ay;
    const den = u.x * ey - u.y * ex;
    if (Math.abs(den) < 1e-9) continue;
    const t = ((ax - p.x) * ey - (ay - p.y) * ex) / den;
    const s = ((ax - p.x) * u.y - (ay - p.y) * u.x) / den;
    if (s < 0 || s > 1 || t < t0 || t > t1) continue;
    if (!best || Math.abs(t) < Math.abs(best.t)) best = { t, x: p.x + u.x * t, y: p.y + u.y * t };
  }
  return best;
}

/* ------------------------------------------------------------------ reading */

interface Anchor {
  label: StationLabel;
  tip: Vec;
  point: Vec | null; // the point on a centreline it names (null: off every centreline)
  rail: Rail | null;
  /** The centreline's direction there (either way along it). */
  tangent: Vec | null;
}

/**
 * Read the stationing of a plan. `comps`/`labels` are the ink's components (working px);
 * `luma` the plan's lightness, at `lumaK` pixels per image px (1: full resolution).
 */
export function readStationing(plan: VectorPlan, comps: Component[], labels: Int32Array, luma: Luma, lumaK: number, engine = new IntentEngine(plan), leaveOut: string | null = null): Stationing {
  const t0 = performance.now();
  const out: Stationing = { alignments: [], labels: [], leaders: [], groups: [], traces: [], anchorsSeen: [], ticks: [], ms: 0 };
  if (!ocrReady()) return out;
  const k = plan.k;
  const lines = findTextLines(comps, labels, plan.cw);
  // 1. the station labels
  const found: { label: StationLabel; line: TextLine }[] = [];
  for (const line of lines) {
    if (line.glyphs.length < 4) continue;
    const { boxes, metrics } = lineGlyphs(luma, line, k / lumaK);
    const P: Float32Array[] = [];
    for (const b of boxes) {
      const p = glyphProbs(glyphFeatures(luma, b, metrics));
      if (!p) return out;
      P.push(p);
    }
    const r = parseStationLine(P, boxes, metrics.H, metrics.base);
    if (!r || r.conf < MIN_CONF) continue;
    const label: StationLabel = { ...r, box: [line.minX / k, line.minY / k, (line.maxX + 1) / k, (line.maxY + 1) / k], H: line.H / k };
    out.labels.push(label);
    found.push({ label, line });
  }
  // 2. each followed along its leader to the point it names
  const anchors: Anchor[] = [];
  const ticked = new Map<number, number>();
  for (const { label, line } of found) {
    if (leaveOut !== null && label.text === leaveOut) continue; // (validation: as if the plan had no such label)
    const H = label.H,
      mid = (line.base - 0.5 * line.H) / k;
    // the leader leaves the label's own line, or (a block of lines, "MIRA LOMA … / … / SC1 ℄ STA: …") one of its block
    const block = [line, ...blockOf(line, lines)];
    let leader: ReturnType<typeof followLeader> = null;
    for (const bl of block) {
      const m = bl === line ? mid : (bl.base - 0.5 * bl.H) / k;
      const tries = ([1, -1] as const).map((side) => followLeader(plan, engine, { x: side > 0 ? (bl.maxX + 1) / k : bl.minX / k, y: m }, side, H)).filter((l): l is NonNullable<typeof l> => !!l);
      // a label has one leader: its landing starts right at the text
      tries.sort((a, b) => a.gap - b.gap);
      if (tries.length) {
        leader = tries[0]!;
        break;
      }
    }
    if (!leader) {
      out.groups.push(`no leader: ${label.text} (line ${(line.minX / k).toFixed(2)}–${((line.maxX + 1) / k).toFixed(2)} at ${mid.toFixed(2)}, H ${H.toFixed(2)}, block of ${block.length})`);
      continue;
    }
    // the point it names: a curve point's circle on its line near its end (the circle's centre), or
    // where it meets a centreline (a dash-dot line before any other), nearest its end
    const found = circleAt(plan, luma, lumaK, leader.tip, leader.dir, H);
    const circle = found ? found.c : null;
    const R = Math.max(10, 1.2 * H);
    let best: { point: Vec; rail: Rail; cost: number } | null = null;
    const cands: string[] = [];
    const seenRails = new Set<number>();
    for (const id of engine.segsNear(circle ?? leader.tip, R)) {
      if (leader.segs.has(id)) continue;
      const s = plan.segs[id]!;
      if (s.a === s.b) continue;
      const rail = engine.rails.railOf(id);
      if (seenRails.has(rail.id)) continue;
      seenRails.add(rail.id);
      if (rail.len < 80) continue;
      // the leader itself (or a line running on in its direction) is not the line it points at
      const near = projectOnRail(rail, circle ?? leader.tip, R);
      if (!near || Math.abs(near.tx * leader.dir.x + near.ty * leader.dir.y) > Math.cos((12 * Math.PI) / 180)) continue;
      let point: Vec, cost: number;
      if (circle) {
        // the circle sits on the line it marks
        const pr = projectOnRail(rail, circle, 6);
        if (!pr) continue;
        point = { x: circle.x, y: circle.y };
        cost = pr.d;
      } else {
        const hit = rayHit(rail.pts, leader.tip, leader.dir, -R, 0.5 * R);
        if (!hit) continue;
        point = { x: hit.x, y: hit.y };
        cost = Math.abs(hit.t);
      }
      // a stationed line is ticked at its stations, and drawn dash-dot
      const ticks = ticksOn(plan, rail, ticked);
      if (ticks < 2) cost += 60;
      if (!rail.dashed) cost += 20;
      cands.push(`#${rail.id}${rail.dashed ? 'd' : ''} t${ticks} c${cost.toFixed(1)}`);
      if (!best || cost < best.cost) best = { point, rail, cost };
    }
    // a station tick where it meets the line: the point is exactly on the tick
    if (best && !circle) {
      const snapped = tickAt(plan, best.rail, best.point, H);
      if (snapped) best.point = snapped;
    }
    const tangent = found ? found.dir : best ? lineDirAt(plan, best.point, 2 / plan.k, leader.dir) : null;
    anchors.push({ label, tip: leader.tip, point: best ? best.point : null, rail: best ? best.rail : null, tangent });
    out.anchorsSeen.push({ text: label.text, point: best ? [best.point.x, best.point.y] : null, tangent: tangent ? [tangent.x, tangent.y] : null, circle: !!circle });
    out.leaders.push({ text: label.text, start: [leader.start.x, leader.start.y], tip: [leader.tip.x, leader.tip.y], point: best ? [best.point.x, best.point.y] : null, rail: best ? `#${best.rail.id} ${Math.round(best.rail.len)}px${best.rail.dashed ? ' dashed' : ''} [${cands.join(', ')}]` : null });
  }
  // 3. each alignment: its labelled points in station order, joined one stretch at a time along its
  //    centreline (one tangent or one curve between each two), every stretch as long as the plan's
  //    scale says the stations it spans must be
  const onLine = anchors.filter((a) => a.point && a.label.kind !== 'CL');
  const byName = new Map<string, Anchor[]>();
  const unnamed: Anchor[] = [];
  for (const a of onLine) {
    if (a.label.name) byName.set(a.label.name, [...(byName.get(a.label.name) ?? []), a]);
    else unnamed.push(a);
  }
  const built: Built[] = [];
  for (const [name, list] of byName) {
    const b = chainOf(plan, list, name, out.groups);
    if (b) built.push(b);
  }
  // unnamed labels: on a named alignment where its measure puts them, or else a line of their own
  const rest: Anchor[] = [];
  for (const a of unnamed) {
    let joined = false;
    for (const b of built) {
      const q = projectOnLine(b, a.point!);
      if (q.d > 2.5 || q.over > 0) continue;
      const resid = b.origin + q.s / b.ppf - a.label.feet;
      if (Math.abs(resid) <= tolFt(b.ppf)) {
        b.anchors.push({ s: q.s, feet: a.label.feet, resid, text: a.label.text, x: a.point!.x, y: a.point!.y });
        joined = true;
        break;
      }
    }
    if (!joined) rest.push(a);
  }
  if (rest.length >= 2) {
    const b = chainOf(plan, rest, null, out.groups);
    if (b) built.push(b);
  }
  // the plan's scale: what the well-borne alignments agree on; one borne by two labels only must agree with it
  const strong = built.filter((b) => b.anchors.length >= 3).map((b) => b.ppf);
  strong.sort((p, q) => p - q);
  const scale = strong.length ? strong[strong.length >> 1]! : null;
  for (const b of built) {
    if (scale !== null && Math.abs(b.ppf - scale) > 0.03 * scale) continue; // a drawing has one scale
    if (b.anchors.length < 3) {
      // two labels fix no scale of their own worth measuring far by: the plan's, if they agree with it
      if (scale === null) continue;
      const origin = b.anchors.reduce((q, a) => q + a.feet - a.s / scale, 0) / b.anchors.length;
      const anchors = b.anchors.map((a) => ({ ...a, resid: origin + a.s / scale - a.feet }));
      if (anchors.some((a) => Math.abs(a.resid) > tolFt(scale))) continue;
      out.alignments.push({ name: b.name, pts: b.pts, cum: b.cum, origin, sign: 1, pxPerFt: scale, anchors, knots: [], tickEvery: null, reach: 0 });
      continue;
    }
    out.alignments.push({ name: b.name, pts: b.pts, cum: b.cum, origin: b.origin, sign: 1, pxPerFt: b.ppf, anchors: b.anchors, knots: [], tickEvery: null, reach: 0 });
  }
  // two alignments never share a drawn line: where one's line (followed past its labels) runs onto
  // another's, it has left its own, and ends there
  for (const al of out.alignments) for (const other of out.alignments) if (other !== al) cutWhereOnto(al, other);
  for (const al of out.alignments) {
    const ts = ticksAlong(plan, al.pts);
    const k = knotsOf(al, ts);
    al.knots = k.knots;
    al.tickEvery = k.every;
    al.reach = k.every ? Math.min(k.every, 10) * al.pxPerFt : 5 * al.pxPerFt;
    out.ticks.push(`${al.name ?? '?'}: every ${k.every ?? '—'} ft; knots ${k.knots.map((q) => `${q.feet.toFixed(q.tick ? 0 : 2)}${q.tick ? '' : '*'}`).join(' ')}${k.note ? ` (${k.note})` : ''}`);
  }
  out.ms = Math.round(performance.now() - t0);
  return out;
}

function cumOf(pts: number[]): number[] {
  const cum = [0];
  for (let i = 2; i < pts.length; i += 2) cum.push(cum[cum.length - 1]! + Math.hypot(pts[i]! - pts[i - 2]!, pts[i + 1]! - pts[i - 1]!));
  return cum;
}

/** Nearest point of a polyline (with its arc lengths) to p: its arc length, distance, and how far past an end p lies. */
function projectOnLine(line: { pts: number[]; cum: number[] }, p: Vec): { s: number; d: number; over: number } {
  return projectOnAlignment(line as Alignment, p);
}

const tolFt = (ppf: number) => Math.max(0.3, 2.5 / ppf);

interface Built {
  name: string | null;
  pts: number[];
  cum: number[];
  origin: number; // station at arc 0 (stations grow along pts)
  ppf: number;
  anchors: Alignment['anchors'];
}

/**
 * An alignment from its labelled points: in station order, each joined to the next by the one
 * tangent or curve between them that the plan's ink bears out; the stretches whose length agrees
 * with the stations they span (at the scale they agree on) make the alignment — the longest run of
 * them, of two stretches at least (three labels). Its centreline then runs on past its first and
 * last labelled points as far as it is drawn.
 */
function chainOf(plan: VectorPlan, list: Anchor[], name: string | null, log: string[]): Built | null {
  // one label per station (two labels naming one station at two places is no evidence)
  const bySta = new Map<number, Anchor[]>();
  for (const a of list) {
    const key = Math.round(a.label.feet * 100);
    bySta.set(key, [...(bySta.get(key) ?? []), a]);
  }
  const pts: Anchor[] = [];
  for (const same of bySta.values()) {
    const far = same.some((x) => Math.hypot(x.point!.x - same[0]!.point!.x, x.point!.y - same[0]!.point!.y) > 3);
    if (!far) pts.push(same[0]!);
  }
  pts.sort((a, b) => a.label.feet - b.label.feet);
  if (pts.length < 2) return null;
  const spans: ({ arc: ArcStretch; ppf: number } | null)[] = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    const arc = arcBetween(plan, pts[i]!.point!, pts[i + 1]!.point!, 0.6, pts[i]!.tangent, pts[i + 1]!.tangent);
    if (!arc) {
      const free = arcBetween(plan, pts[i]!.point!, pts[i + 1]!.point!, 0.6);
      const deg = (t: Vec | null) => (t ? ((Math.atan2(t.y, t.x) * 180) / Math.PI).toFixed(1) : '-');
      log.push(`  ${pts[i]!.label.station}→${pts[i + 1]!.label.station}: no arc (without tangents: ${free ? `len ${free.len.toFixed(1)} bow ${free.bow.toFixed(1)} support ${free.support.toFixed(2)}` : 'none'}; tangents ${deg(pts[i]!.tangent)} ${deg(pts[i + 1]!.tangent)}, chord ${((Math.atan2(pts[i + 1]!.point!.y - pts[i]!.point!.y, pts[i + 1]!.point!.x - pts[i]!.point!.x) * 180) / Math.PI).toFixed(1)})`);
    }
    spans.push(arc ? { arc, ppf: arc.len / (pts[i + 1]!.label.feet - pts[i]!.label.feet) } : null);
  }
  const ppfs = spans.filter((x): x is NonNullable<typeof x> => !!x).map((x) => x.ppf).sort((p, q) => p - q);
  if (!ppfs.length) {
    log.push(`${name ?? '(unnamed)'}: ${pts.map((p) => p.label.station).join(' ')} → no stretch borne out`);
    return null;
  }
  const med = ppfs[ppfs.length >> 1]!;
  const ok = spans.map((x, i) => !!x && Math.abs(x.arc.len / med - (pts[i + 1]!.label.feet - pts[i]!.label.feet)) <= tolFt(med));
  // the longest run of agreeing stretches
  let bestFrom = -1,
    bestLen = 0;
  for (let i = 0; i < ok.length; ) {
    if (!ok[i]) {
      i++;
      continue;
    }
    let j = i;
    while (j < ok.length && ok[j]) j++;
    if (j - i > bestLen) {
      bestLen = j - i;
      bestFrom = i;
    }
    i = j;
  }
  log.push(`${name ?? '(unnamed)'}: ${pts.map((p) => p.label.station).join(' ')} → stretches ${spans.map((x, i) => (x ? `${x.ppf.toFixed(2)}${ok[i] ? '' : '✗'}` : '—')).join(' ')}`);
  if (bestLen < 1) return null;
  const run = pts.slice(bestFrom, bestFrom + bestLen + 1);
  // the scale that fits the run's labels best (least squares through the origin of its first)
  let num = 0,
    den = 0,
    acc = 0;
  const along: number[] = [0];
  for (let i = bestFrom; i < bestFrom + bestLen; i++) {
    acc += spans[i]!.arc.len;
    along.push(acc);
  }
  for (let i = 1; i < run.length; i++) {
    const dF = run[i]!.label.feet - run[0]!.label.feet;
    num += along[i]! * dF;
    den += dF * dF;
  }
  const ppf = num / den;
  // the centreline: the stretches, then on past each end as far as it is drawn
  const body: number[] = [];
  for (let i = bestFrom; i < bestFrom + bestLen; i++) {
    const p = spans[i]!.arc.pts;
    for (let j = body.length ? 2 : 0; j < p.length; j += 2) body.push(p[j]!, p[j + 1]!);
  }
  const headDir = unit(body[2]! - body[0]!, body[3]! - body[1]!);
  const n = body.length;
  const tailDir = unit(body[n - 2]! - body[n - 4]!, body[n - 1]! - body[n - 3]!);
  const before = extendLine(plan, { x: body[0]!, y: body[1]! }, { x: -headDir.x, y: -headDir.y });
  const after = extendLine(plan, { x: body[n - 2]!, y: body[n - 1]! }, tailDir);
  const all: number[] = [];
  for (let i = before.length - 1; i >= 1; i--) all.push(before[i]!.x, before[i]!.y);
  const s0 = cumOf(all.length ? [...all, body[0]!, body[1]!] : [body[0]!, body[1]!]);
  const lead = s0[s0.length - 1]!; // arc length before the first labelled point
  all.push(...body);
  for (let i = 1; i < after.length; i++) all.push(after[i]!.x, after[i]!.y);
  const cum = cumOf(all);
  const origin = run[0]!.label.feet - lead / ppf;
  const anchors = run.map((a, i) => {
    const s = lead + along[i]!;
    return { s, feet: a.label.feet, resid: origin + s / ppf - a.label.feet, text: a.label.text, x: a.point!.x, y: a.point!.y };
  });
  if (anchors.some((x) => Math.abs(x.resid) > tolFt(ppf))) return null;
  return { name, pts: all, cum, origin, ppf, anchors };
}

const unit = (x: number, y: number): Vec => {
  const l = Math.hypot(x, y) || 1;
  return { x: x / l, y: y / l };
};

/** The centreline on past a labelled end, as far as it is drawn (followed, straight or curving as it runs). */
function extendLine(plan: VectorPlan, p: Vec, u: Vec): Vec[] {
  const tr = traceCentreline(plan, p, u, true);
  const out: Vec[] = [];
  for (let i = 0; i < tr.length; i += 2) out.push({ x: tr[i]!, y: tr[i + 1]! });
  return out;
}

/** The lines standing with a line in one block of text: stacked close, one height, one edge aligned. */
function blockOf(line: TextLine, lines: TextLine[]): TextLine[] {
  const out: TextLine[] = [];
  let edge = [line];
  const within = (a: TextLine, b: TextLine) => {
    const h = Math.max(a.H, b.H);
    if (Math.max(a.H, b.H) > 1.3 * Math.min(a.H, b.H)) return false;
    const dy = Math.abs(a.base - b.base);
    if (dy > 2.2 * h || dy < 0.8 * h) return false;
    return Math.abs(a.minX - b.minX) <= 1.5 * h || Math.abs(a.maxX - b.maxX) <= 1.5 * h;
  };
  for (let round = 0; round < 6 && edge.length; round++) {
    const next: TextLine[] = [];
    for (const e of edge) for (const l of lines) if (l !== line && !out.includes(l) && !next.includes(l) && within(e, l)) next.push(l);
    out.push(...next);
    edge = next;
  }
  // nearest first
  out.sort((a, b) => Math.abs(a.base - line.base) - Math.abs(b.base - line.base));
  return out;
}

/**
 * The points of known station along an alignment: its labels, and its ticks. Between its labels the
 * fit puts every tick at a station; if they fall on the multiples of one interval (5 ft, 10 ft …)
 * — nearly every multiple ticked, and no tick off them — that is the interval they are drawn at, and
 * each is a point of known station. Beyond the labels the ticks are followed outward one by one,
 * each the next multiple on, as long as each lies about an interval's length from the last.
 */
function knotsOf(al: Alignment, ticks: number[]): { knots: Alignment['knots']; every: number | null; note: string } {
  const ppf = al.pxPerFt;
  const labels = al.anchors.map((a) => ({ s: a.s, feet: a.feet, tick: false })).sort((a, b) => a.s - b.s);
  const s0 = labels[0]!.s,
    s1 = labels[labels.length - 1]!.s;
  const fit = (s: number) => al.origin + s / ppf;
  const tol = Math.max(0.2, 1.5 / ppf);
  const inSpan = ticks.filter((t) => t > s0 + 2 && t < s1 - 2);
  let every: number | null = null;
  for (const d of [1, 2, 2.5, 5, 10, 20, 25, 50, 100]) {
    if (d * ppf < 12) continue; // ticks closer than this are not told apart
    const f0 = fit(s0),
      f1 = fit(s1);
    const multiples: number[] = [];
    for (let m = Math.ceil((f0 + 0.5) / d) * d; m < f1 - 0.5; m += d) multiples.push(m);
    if (multiples.length < 2) break;
    const on = new Set<number>();
    for (const t of inSpan) {
      const f = fit(t),
        m = Math.round(f / d) * d;
      if (Math.abs(f - m) <= tol) on.add(m);
    }
    // nearly every multiple ticked
    if (on.size >= Math.max(2, 0.7 * multiples.length)) {
      every = d;
      break;
    }
  }
  if (every === null) return { knots: labels, every: null, note: '' };
  const d = every;
  const knots = [...labels];
  // the ticks between the labels
  for (const t of inSpan) {
    const f = fit(t),
      m = Math.round(f / d) * d;
    if (Math.abs(f - m) <= tol && !knots.some((q) => Math.abs(q.s - t) < 0.5 * d * ppf)) knots.push({ s: t, feet: m, tick: true });
  }
  // beyond them, tick by tick
  const walk = (dir: 1 | -1) => {
    let last = dir > 0 ? knots.reduce((a, b) => (b.s > a.s ? b : a)) : knots.reduce((a, b) => (b.s < a.s ? b : a));
    const pool = ticks.filter((t) => (dir > 0 ? t > last.s + 2 : t < last.s - 2)).sort((a, b) => dir * (a - b));
    for (const t of pool) {
      const gap = Math.abs(t - last.s) / ppf; // ft
      if (gap > 2.5 * d) break; // the ticks stop (or the line was lost): no further
      const f = last.feet + dir * gap,
        m = Math.round(f / d) * d;
      // the next round station on (or the one after, a tick missed), right where its length along puts it
      if (Math.abs(f - m) > tol + 0.01 * gap) continue; // not a tick of it (a crossing, a symbol, another line's)
      const step = Math.round((dir * (m - last.feet)) / d);
      if (step < 1 || step > 2) continue;
      const kq = { s: t, feet: m, tick: true };
      knots.push(kq);
      last = kq;
    }
  };
  walk(1);
  walk(-1);
  knots.sort((a, b) => a.s - b.s);
  return { knots, every: d, note: '' };
}

/**
 * Cut an alignment's line back where it runs along another's (within a couple of pixels, for more
 * than a few) outside its own labelled stretch: from there on it is the other's line.
 */
function cutWhereOnto(al: Alignment, other: Alignment): void {
  if (!al.anchors.length) return;
  const s0 = Math.min(...al.anchors.map((a) => a.s)),
    s1 = Math.max(...al.anchors.map((a) => a.s));
  const n = al.pts.length / 2;
  const on = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const q = projectOnAlignment(other, { x: al.pts[2 * i]!, y: al.pts[2 * i + 1]! });
    on[i] = q.d <= 2.5 && q.over === 0 ? 1 : 0;
  }
  // the first run of shared line (8 px or more) beyond each end of its labelled stretch
  let lo = 0,
    hi = n - 1;
  for (let i = 0; i < n; i++) {
    if (al.cum[i]! <= s1) continue;
    let j = i;
    while (j < n && on[j]) j++;
    if (j > i && al.cum[j - 1]! - al.cum[i]! >= 8) {
      hi = i;
      break;
    }
  }
  for (let i = n - 1; i >= 0; i--) {
    if (al.cum[i]! >= s0) continue;
    let j = i;
    while (j >= 0 && on[j]) j--;
    if (j < i && al.cum[i]! - al.cum[j + 1]! >= 8) {
      lo = i;
      break;
    }
  }
  if (lo === 0 && hi === n - 1) return;
  const shift = al.cum[lo]!;
  al.pts = al.pts.slice(2 * lo, 2 * hi + 2);
  al.cum = al.cum.slice(lo, hi + 1).map((c) => c - shift);
  al.origin += shift / al.pxPerFt;
  for (const a of al.anchors) a.s -= shift;
}

/* ------------------------------------------------------------------ measuring */

/** The station at arc length s along an alignment. */
export function stationAt(al: Alignment, s: number): number | null {
  const K = al.knots;
  if (!K.length) return null;
  const first = K[0]!,
    last = K[K.length - 1]!;
  if (s < first.s - al.reach || s > last.s + al.reach) return null; // beyond what the plan measures
  if (s <= first.s) return first.feet - (first.s - s) / al.pxPerFt;
  if (s >= last.s) return last.feet + (s - last.s) / al.pxPerFt;
  // between the two knots about it, in proportion (stations are lengths along the line)
  let lo = 0,
    hi = K.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (K[mid]!.s <= s) lo = mid;
    else hi = mid;
  }
  const a = K[lo]!,
    b = K[hi]!;
  return a.feet + ((b.feet - a.feet) * (s - a.s)) / (b.s - a.s || 1);
}

/** Nearest point of an alignment's centreline to p: its arc length and distance. */
export function projectOnAlignment(al: Alignment, p: Vec): { s: number; d: number; over: number } {
  const { pts, cum } = al;
  let best = { s: 0, d: Infinity, over: 0 };
  const n = pts.length / 2;
  for (let i = 0; i + 1 < n; i++) {
    const ax = pts[2 * i]!,
      ay = pts[2 * i + 1]!,
      bx = pts[2 * i + 2]!,
      by = pts[2 * i + 3]!;
    const ex = bx - ax,
      ey = by - ay;
    const l2 = ex * ex + ey * ey || 1;
    const tr = ((p.x - ax) * ex + (p.y - ay) * ey) / l2;
    const t = Math.max(0, Math.min(1, tr));
    const d = Math.hypot(p.x - (ax + ex * t), p.y - (ay + ey * t));
    if (d < best.d) {
      const l = Math.sqrt(l2);
      const over = i === 0 && tr < 0 ? -tr * l : i === n - 2 && tr > 1 ? (tr - 1) * l : 0;
      best = { s: cum[i]! + t * l, d, over };
    }
  }
  return best;
}

export interface MarkStations {
  alignment: Alignment;
  from: number; // feet, the lower
  to: number;
  /** The centreline runs through the mark (else the mark lies beside it, measured square off it). */
  on: boolean;
}

/**
 * The stations a mark spans, along each alignment that runs through it (the stretch of centreline
 * inside its outline) — or, when none does, along the one it lies beside (its outline measured
 * square onto that centreline). Empty when the plan's stationing does not reach it.
 */
export function stationsOfOutline(st: { alignments: Alignment[] }, poly: number[]): MarkStations[] {
  const out: MarkStations[] = [];
  // the mark's own length (the farthest two points of it): a mark lies along a line when the line
  // runs through most of that length, never when it only crosses it (a trench across a duct bank)
  let across = 0;
  for (let i = 0; i < poly.length; i += 2) for (let j = i + 2; j < poly.length; j += 2) across = Math.max(across, Math.hypot(poly[i]! - poly[j]!, poly[i + 1]! - poly[j + 1]!));
  for (const al of st.alignments) {
    const inside = insideStretch(al, poly);
    if (!inside) continue;
    if (inside[1] - inside[0] < 0.6 * across) continue;
    const a = stationAt(al, inside[0]),
      b = stationAt(al, inside[1]);
    if (a === null || b === null) continue; // it runs past what the plan measures
    out.push({ alignment: al, from: Math.min(a, b), to: Math.max(a, b), on: true });
  }
  if (out.length) return out;
  // beside a centreline (drawn a little off it): within a couple of feet of it, and wholly along it (never past its ends)
  let best: { al: Alignment; d: number; s0: number; s1: number } | null = null;
  for (const al of st.alignments) {
    const reach = 2.5 * al.pxPerFt;
    let s0 = Infinity,
      s1 = -Infinity,
      dMin = Infinity,
      ok = true;
    for (let i = 0; i < poly.length; i += 2) {
      const pr = projectOnAlignment(al, { x: poly[i]!, y: poly[i + 1]! });
      if (pr.over > 0.5 * al.pxPerFt) ok = false;
      s0 = Math.min(s0, pr.s);
      s1 = Math.max(s1, pr.s);
      dMin = Math.min(dMin, pr.d);
    }
    if (!ok || dMin > reach) continue;
    if (!best || dMin < best.d) best = { al, d: dMin, s0, s1 };
  }
  if (best) {
    const a = stationAt(best.al, best.s0),
      b = stationAt(best.al, best.s1);
    if (a === null || b === null) return out;
    out.push({ alignment: best.al, from: Math.min(a, b), to: Math.max(a, b), on: false });
  }
  return out;
}

/** The stretch [s0, s1] of an alignment's centreline inside a closed outline (null: none, or it runs off the traced line). */
function insideStretch(al: Alignment, poly: number[]): [number, number] | null {
  const { pts, cum } = al;
  const n = pts.length / 2;
  const m = poly.length / 2;
  if (m < 3) return null;
  let s0 = Infinity,
    s1 = -Infinity;
  for (let i = 0; i + 1 < n; i++) {
    const ax = pts[2 * i]!,
      ay = pts[2 * i + 1]!,
      bx = pts[2 * i + 2]!,
      by = pts[2 * i + 3]!;
    const l = cum[i + 1]! - cum[i]!;
    // the params where this piece crosses the outline, then which parts lie inside
    const ts = [0, 1];
    for (let j = 0; j < m; j++) {
      const cx = poly[2 * j]!,
        cy = poly[2 * j + 1]!,
        dx = poly[(2 * j + 2) % (2 * m)]!,
        dy = poly[(2 * j + 3) % (2 * m)]!;
      const ex = bx - ax,
        ey = by - ay,
        fx = dx - cx,
        fy = dy - cy;
      const den = ex * fy - ey * fx;
      if (Math.abs(den) < 1e-12) continue;
      const t = ((cx - ax) * fy - (cy - ay) * fx) / den;
      const u = ((cx - ax) * ey - (cy - ay) * ex) / den;
      if (t > 0 && t < 1 && u >= 0 && u <= 1) ts.push(t);
    }
    ts.sort((a, b) => a - b);
    for (let q = 0; q + 1 < ts.length; q++) {
      const tm = (ts[q]! + ts[q + 1]!) / 2;
      if (!pointInPoly(ax + (bx - ax) * tm, ay + (by - ay) * tm, poly)) continue;
      s0 = Math.min(s0, cum[i]! + ts[q]! * l);
      s1 = Math.max(s1, cum[i]! + ts[q + 1]! * l);
    }
  }
  if (!(s1 - s0 > 2)) return null;
  // the mark runs past the end of the traced centreline: its stations there are not measured
  const total = cum[n - 1]!;
  if ((s0 <= 0.5 && pointInPoly(pts[0]!, pts[1]!, poly)) || (s1 >= total - 0.5 && pointInPoly(pts[2 * n - 2]!, pts[2 * n - 1]!, poly))) return null;
  return [s0, s1];
}

function pointInPoly(x: number, y: number, poly: number[]): boolean {
  let inside = false;
  const m = poly.length / 2;
  for (let i = 0, j = m - 1; i < m; j = i++) {
    const xi = poly[2 * i]!,
      yi = poly[2 * i + 1]!,
      xj = poly[2 * j]!,
      yj = poly[2 * j + 1]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
/** The reader's inner steps, for the dev probes (scripts/, .dbg/): `log` collects what circleAt weighed when set. */
export const __debug: { followLeader: typeof followLeader; circleAt: typeof circleAt; marchInk: typeof marchInk; log: string[] | null } = { followLeader, circleAt, marchInk, log: null };
